import {Food, IMealFood, isIMealFood, MealFood, Unit} from './food';
import {UUID} from 'angular2-uuid';
import {Base} from './Base';
import {Meal} from './Meal';
import {FoodProduct, IMealFoodProduct, isIMealFoodProduct, MealFoodProduct} from './FoodProduct';
import {MealItem} from './MealItem';
import {ImageService} from '../image.service';
import {CategoryStatistics} from './Statistics';
import {ILocalUserRecipe} from "./IngredientMapping/SpeechToMealResponse";

export class Recipe extends Base {
  id: string;
  name: string;
  description: string;
  createdAt: Date; // for sorting options
  ingredients: (MealFood | MealFoodProduct)[];
  portions: number;
  // that's the id of the user's own image
  mainImage: string;
  // that's the idoplanty recipe images
  idoplantyMainImageIndex: number;
  idoplantyImages: RecipeImage[];
  source: string;
  sourceProvider: string;
  baseUnit = 'g';
  private readonly defaultImage = '/assets/icon/categories/Recipe.svg';

  constructor(i?: IRecipe) {
    super();
    this.ingredients = [];
    if (i !== undefined) {
      this.id = i.id;
      this.name = i.name;
      this.description = i.description;
      this.createdAt = i.createdAt;
      this.portions = i.portions;
      this.mainImage = i.mainImage;
      this.idoplantyMainImageIndex = i.idoplantyMainImageIndex;
      this.idoplantyImages = i.idoplantyImages;
      this.source = i.source;
      this.sourceProvider = i.sourceProvider;
      if (i.ingredients) {
        i.ingredients.forEach(ingredient => {
          if (isIMealFood(ingredient)) {
            this.ingredients.push(new MealFood(ingredient));
          }
          if (isIMealFoodProduct(ingredient)) {
            this.ingredients.push(new MealFoodProduct(ingredient));
          }
        });
      }
    } else {
      this.id = UUID.UUID();
      this.createdAt = new Date();
    }
  }

  get hasDefaultImage(): boolean {
    return this.imgUrl === this.defaultImage;
  }

  get emissions(): number {
    if (this.ingredients !== undefined) {
      let total = 0;
      this.ingredients.forEach(i => {
        total += i.emissions;
      });
      return total;
    } else {
      return 0;
    }
  }

  get emissionsPerPortion(): number {
    if (!this.portions) {
      return 0;
    }
    return this.emissions / this.portions;
  }

  get calories(): number {
    if (this.ingredients !== undefined) {
      let total = 0;
      this.ingredients.forEach(i => {
        total += i.calories;
      });
      return total;
    } else {
      return 0;
    }
  }

  get caloriesPerPortion(): number {
    if (!this.portions) {
      return 0;
    }
    return this.calories / this.portions;
  }

  get amountInGram(): number {
    if (this.ingredients !== undefined) {
      let total = 0;
      this.ingredients.forEach(i => {
        total += i.unit.toGram(i.amount);
      });
      return total;
    } else {
      return 0;
    }
  }

  get amountInGramPerPortion(): number {
    return this.amountInGram / this.portions;
  }

  get carbsKnown(): boolean {
    if (!this.ingredients || this.ingredients.length === 0) {
      return false;
    }
    return this.ingredients.every(i => i.carbsKnown);
  }

  get carbs(): number {
    if (!this.ingredients) {
      return 0;
    }
    let total = 0;
    this.ingredients.forEach(i => {
      if (i.carbsKnown) {
        total += i.carbs;
      }
    });
    return total;
  }

  get carbsPerPortion(): number {
    if (!this.portions) {
      return 0;
    }
    return this.carbs / this.portions;
  }

  get fatKnown(): boolean {
    if (!this.ingredients || this.ingredients.length === 0) {
      return false;
    }
    return this.ingredients.every(i => i.fatKnown);
  }

  get fat(): number {
    if (!this.ingredients) {
      return 0;
    }
    let total = 0;
    this.ingredients.forEach(i => {
      if (i.fatKnown) {
        total += i.fat;
      }
    });
    return total;
  }

  get fatPerPortion(): number {
    if (!this.portions) {
      return 0;
    }
    return this.fat / this.portions;
  }

  get proteinKnown(): boolean {
    if (!this.ingredients || this.ingredients.length === 0) {
      return false;
    }
    return this.ingredients.every(i => i.proteinKnown);
  }

  get protein(): number {
    if (!this.ingredients) {
      return 0;
    }
    let total = 0;
    this.ingredients.forEach(i => {
      if (i.proteinKnown) {
        total += i.protein;
      }
    });
    return total;
  }

  get proteinPerPortion(): number {
    if (!this.portions) {
      return 0;
    }
    return this.protein / this.portions;
  }

  get macrosKnown(): boolean {
    return this.carbsKnown && this.proteinKnown && this.fatKnown;
  }

  get caloriesPer100g(): number {
    return this.calories / this.amountInGram * 100;
  }

  get emissionsPer100g(): number {
    return this.emissions / this.amountInGram * 100;
  }

  get emissionsPerCalorie(): number {
    return this.emissions / this.calories;
  }

  get footprintPerKg(): number {
    return this.emissionsPer100g / 1000 * 10;
  }

  get carbsPer100g(): number {
    return this.carbs / this.amountInGram * 100;
  }

  get fatPer100g(): number {
    return this.fat / this.amountInGram * 100;
  }

  get proteinPer100g(): number {
    return this.protein / this.amountInGram * 100;
  }

  set rename(input: string) {
    this.name = input;
  }

  get unit(): Unit {
    return new Unit({baseUnit: this.baseUnit, name: 'Portion', conversionToGram: this.amountInGram / this.portions});
  }

  get categoryStatisticsEmissions(): CategoryStatistics {
    const ret = new CategoryStatistics();
    this.ingredients.forEach(i => ret.add(i.categoryStatisticEmissions.toCategoryStatistic()));
    return ret;
  }

  get categoryStatisticsCalories(): CategoryStatistics {
    const ret = new CategoryStatistics();
    this.ingredients.forEach(i => ret.add(i.categoryStatisticCalories.toCategoryStatistic()));
    return ret;
  }

  private _imgUrl: string;

  get imgUrl(): string {
    return this._imgUrl ? this._imgUrl : this.defaultImage;
  }

  set imgUrl(value: string) {
    this._imgUrl = value;
  }

  private _imgUrlThumbnail: string;

  get imgUrlThumbnail(): string {
    return this._imgUrlThumbnail ? this._imgUrlThumbnail : this.defaultImage;
  }

  set imgUrlThumbnail(value: string) {
    this._imgUrlThumbnail = value;
  }

  static deepCopyArray(recipes: Recipe[]): Recipe[] {
    const ret: Recipe[] = [];
    recipes.forEach(element => {
      ret.push(element.deepCopy());
    });
    return ret;
  }

  static fromMeal(meal: Meal): Recipe {
    return this.fromMealItems(meal.itemList);
  }

  static fromMealItems(mealItems: MealItem[]): Recipe {
    const ret: Recipe = new Recipe();
    mealItems.forEach(i => {
      const item = i.toDetailedMealItem();
      if (!(item instanceof MealRecipe)) {
        ret.ingredients.push(item);
      }
    });
    return ret;
  }

  toInterface(): IRecipe {
    const ingredients: (IMealFood | IMealFoodProduct)[] = [];
    this.ingredients.forEach(ingredient => {
      ingredients.push(ingredient.toInterface());
    });
    return {
      id: this.id.toString(),
      name: this.name,
      description: this.description,
      createdAt: this.createdAt,
      ingredients,
      portions: this.portions,
      mainImage: this.mainImage,
      idoplantyMainImageIndex: this.idoplantyMainImageIndex,
      idoplantyImages: this.idoplantyImages,
      source: this.source,
      sourceProvider: this.sourceProvider
    };
  }

  deepCopy(): Recipe {
    const ret = new Recipe(this.toInterface());
    if (!this.hasDefaultImage) {
      ret.imgUrl = this.imgUrl;
    }
    return ret;
  }

  equals(recipe: Recipe): boolean {
    return this.id === recipe.id && this.portions === recipe.portions;
  }

  // Checks if also ingredients are same, including last change date of ingredients
  equalsDeep(recipe: Recipe): boolean {
    if (!this.equals(recipe)) {
      return false;
    }
    if (!this.ingredients && !recipe.ingredients) {
      return true;
    }
    if (!this.ingredients || !recipe.ingredients) {
      return false;
    }
    // compare food ingredients
    const thisFoodIngredients = this.ingredients.filter(i => i instanceof MealFood) as MealFood[];
    const otherFoodIngredients = recipe.ingredients.filter(i => i instanceof MealFood) as MealFood[];
    for (const ingredient of thisFoodIngredients) {
      let match = false;
      const otherIngredients = otherFoodIngredients.filter(i => i.food.id);
      for (const otherIngredient of otherIngredients) {
        if (otherIngredient.equalsDeep(ingredient)) {
          match = true;
          break;
        }
      }
      if (!match) {
        return false;
      }
    }
    for (const otherIngredient of otherFoodIngredients) {
      let match = false;
      const thisIngredients = thisFoodIngredients.filter(i => i.food.id);
      for (const thisIngredient of thisIngredients) {
        if (thisIngredient.equalsDeep(otherIngredient)) {
          match = true;
          break;
        }
      }
      if (!match) {
        return false;
      }
    }
    // compare food product ingredients
    const thisFoodProductIngredients = this.ingredients.filter(i => i instanceof MealFoodProduct) as MealFoodProduct[];
    const otherFoodProductIngredients = recipe.ingredients.filter(i => i instanceof MealFoodProduct) as MealFoodProduct[];
    for (const ingredient of thisFoodProductIngredients) {
      let match = false;
      const otherIngredients = otherFoodProductIngredients.filter(i => i.foodProduct.id);
      for (const otherIngredient of otherIngredients) {
        if (otherIngredient.equalsDeep(ingredient)) {
          match = true;
          break;
        }
      }
      if (!match) {
        return false;
      }
    }
    for (const otherIngredient of otherFoodProductIngredients) {
      let match = false;
      const thisIngredients = thisFoodProductIngredients.filter(i => i.foodProduct.id);
      for (const thisIngredient of thisIngredients) {
        if (thisIngredient.equalsDeep(otherIngredient)) {
          match = true;
          break;
        }
      }
      if (!match) {
        return false;
      }
    }
    return true;
  }

  public async fetchRecipeImage(imageService: ImageService): Promise<void> {
    if (this.mainImage) {
      this.fetchRecipeUserImage(imageService);
    }
    if (this.idoplantyImages && this.idoplantyImages.length > 0) {
      this.fetchRecipeIdoplantyImages(imageService);
    }
  }

  public updateIngredientBase(newIngredientBase: Food | FoodProduct, index: number): void {
    if (!this.ingredients[index]) {
      return;
    }
    let newIngredient: MealFood | MealFoodProduct;
    if (this.ingredients[index] instanceof MealFood) {
      if (!(newIngredientBase instanceof Food)) {
        return;
      }
      newIngredient = new MealFood(newIngredientBase);
    }
    if (this.ingredients[index] instanceof MealFoodProduct) {
      if (!(newIngredientBase instanceof FoodProduct)) {
        return;
      }
      newIngredient = new MealFoodProduct(newIngredientBase);
    }
    newIngredient.amount = this.ingredients[index].amount;
    newIngredient.unit = this.ingredients[index].unit;
    this.ingredients[index] = newIngredient;
  }

  public updateMacros(foods: Food[], foodProducts: FoodProduct[]): void {
    if (!this.ingredients || this.ingredients.length === 0) {
      return;
    }
    for (const ingredient of this.ingredients) {
      if (ingredient.macrosKnown) {
        continue;
      }
      if (ingredient instanceof MealFood) {
        const usedFood = foods.find(f => f.id === ingredient.food.id);
        if (!usedFood || !usedFood.macrosKnown) {
          continue;
        }
        ingredient.updateMacros(usedFood.carbsPer100g, usedFood.fatPer100g, usedFood.proteinPer100g);
      } else {
        const usedFoodProduct = foodProducts.find(f => f.id === ingredient.foodProduct.id);
        if (!usedFoodProduct || !usedFoodProduct.macrosKnown) {
          continue;
        }
        ingredient.updateMacros(usedFoodProduct.carbsPer100g, usedFoodProduct.fatPer100g, usedFoodProduct.proteinPer100g);
      }
    }
  }

  public toILocalUserRecipe(): ILocalUserRecipe {
    return {id: this.id, name: this.name};
  }

  private fetchRecipeUserImage(imageService: ImageService): void {
    imageService.getRecipeUserImage(this.id).subscribe({
      next: id => this.imgUrl = id
    });
  }

  private fetchRecipeIdoplantyImages(imageService: ImageService): void {
    this.fetchRecipeIdoplantyImage(imageService, this.idoplantyMainImageIndex, RecipeImageSizeType.Thumbnail);
    this.fetchRecipeIdoplantyImage(imageService, this.idoplantyMainImageIndex, RecipeImageSizeType.Normal);
  }

  private fetchRecipeIdoplantyImage(imageService: ImageService, index: number, size: RecipeImageSizeType): void {
    const image = imageService.getRecipeIdoplantyImage(this.id, index, size);
    if (typeof image === 'string') {
      if (!image) {
        return;
      }
      this.setIdoplantyImage(image, size);
    } else {
      image.subscribe({
        next: resp => {
          const statusCode: number = resp.status;
          if (statusCode === 200 && resp.body) {
            const url = URL.createObjectURL(resp.body);
            imageService.addOrUpdateRecipeIdoplantyImageUrl(this.id, url, size);
            this.setIdoplantyImage(url, size);
          } else {
            if (statusCode === 204) {
              imageService.addOrUpdateRecipeIdoplantyImageUrl(this.id, '', size);
            } else {
              console.log('Something went wrong while downloading idoplanty recipe image from backend');
            }
          }
        },
        error: err => {
          console.log(err);
        }
      });
    }
  }

  private setIdoplantyImage(url: string, size: RecipeImageSizeType): void {
    if (size === RecipeImageSizeType.Thumbnail) {
      this.imgUrlThumbnail = url;
    } else {
      this.imgUrl = url;
    }
  }

}

export interface IRecipe {
  id: string;
  name: string;
  description: string;
  createdAt: Date;
  ingredients: (IMealFood | IMealFoodProduct)[];
  portions: number;
  mainImage: string;
  idoplantyMainImageIndex: number;
  idoplantyImages: RecipeImage[];
  source: string;
  sourceProvider: string;
}

export interface RecipeImage {
  availableSizes: RecipeImageSize[];
  originalSource: string;
}

export interface RecipeImageSize {
  type: RecipeImageSizeType;
  width: number;
  height: number;
}

export enum RecipeImageSizeType {
  Unknown, Thumbnail, Normal, FullSize
}

export function isIRecipe(object: any): object is IRecipe {
  return 'ingredients' in object;
}

export class MealRecipe extends Base {
  amount: number;
  readonly recipe: Recipe;

  constructor(recipe: Recipe | IMealRecipe) {
    super();
    if (recipe instanceof Recipe) {
      this.recipe = recipe;
      this.amount = 1;
    } else {
      this.recipe = new Recipe(recipe.recipe);
      this.amount = recipe.amount;
    }
  }

  get emissions(): number {
    return this.recipe.emissionsPerPortion * this.amount;
  } // Emissions ins grams

  get calories(): number {
    return this.recipe.caloriesPerPortion * this.amount;
  }

  get carbsKnown(): boolean {
    return this.recipe.carbsKnown;
  }

  get carbs(): number {
    return this.carbsKnown ? (this.recipe.carbsPerPortion * this.amount) : 0;
  }

  get fatKnown(): boolean {
    return this.recipe.fatKnown;
  }

  get fat(): number {
    return this.fatKnown ? (this.recipe.fatPerPortion * this.amount) : 0;
  }

  get proteinKnown(): boolean {
    return this.recipe.proteinKnown;
  }

  get protein(): number {
    return this.proteinKnown ? (this.recipe.proteinPerPortion * this.amount) : 0;
  }

  get macrosKnown(): boolean {
    return this.carbsKnown && this.proteinKnown && this.fatKnown;
  }

  get name(): string {
    return this.recipe.name;
  }

  get unit(): Unit {
    return this.recipe.unit;
  }

  get imgUrl(): string {
    return this.recipe.imgUrl;
  }

  get imgUrlThumbnail(): string {
    return this.recipe.imgUrlThumbnail;
  }

  get categoryStatisticsEmissions(): CategoryStatistics {
    const ret = this.recipe.categoryStatisticsEmissions;
    ret.changeByFactor(this.amount / this.recipe.portions);
    return ret;
  }

  get categoryStatisticsCalories(): CategoryStatistics {
    const ret = this.recipe.categoryStatisticsCalories;
    ret.changeByFactor(this.amount / this.recipe.portions);
    return ret;
  }

  static deepCopyArray(mealRecipes: MealRecipe[]): MealRecipe[] {
    const ret: MealRecipe[] = [];
    mealRecipes.forEach(element => {
      ret.push(element.deepCopy());
    });
    return ret;
  }

  toInterface(): IMealRecipe {
    return {amount: this.amount, recipe: this.recipe.toInterface()};
  }

  deepCopy(): MealRecipe {
    return new MealRecipe(this.toInterface());
  }

  equals(mealRecipe: MealRecipe): boolean {
    return this.amount === mealRecipe.amount && this.recipe.id === mealRecipe.recipe.id;
  }
}

export interface IMealRecipe {
  amount: number;
  recipe: IRecipe;
}

export function isIMealRecipe(object: any): object is IMealRecipe {
  return 'recipe' in object;
}
